import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Edit3, Trash2, X, Loader2, BookOpen, Save, Folder, Lightbulb, Sparkles,
  Upload, FileSpreadsheet, CheckCircle2
} from 'lucide-react';
import './Derek.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Suggestions shown in the "Suggested Knowledge to Add" panel.
// Clicking one pre-fills the modal with a title, category, and a helpful
// placeholder prompt so the founder just needs to write their answer.
const KNOWLEDGE_SUGGESTIONS = [
  {
    key: 'pricing',
    title: 'Pricing strategy',
    category: 'pricing',
    placeholder:
      'List every product and its current price, any discounts or trial offers, and the rationale behind the pricing. Example: "Social Go £10 per listing, Social Pro £15 per listing, Social Week £99 per branch per month (first month free for new branches)."',
  },
  {
    key: 'target_customers',
    title: 'Target customer types',
    category: 'customers',
    placeholder:
      'Who are you building for? Describe the sweet-spot customer in one paragraph — agency size, branch count, tech maturity, current marketing setup. Add any customers you explicitly do NOT target.',
  },
  {
    key: 'sales_process',
    title: 'Sales process',
    category: 'sales',
    placeholder:
      'Walk through the end-to-end sale: discovery call → demo → trial → close. Include average cycle length, who is involved at each stage, and the key friction points you see.',
  },
  {
    key: 'product_positioning',
    title: 'Product positioning',
    category: 'positioning',
    placeholder:
      'What makes each product the obvious choice? Example: "Social Week is the only weekly social planner built specifically for multi-branch estate agents." Capture the one-line positioning for every product.',
  },
  {
    key: 'revenue_goals',
    title: 'Revenue goals',
    category: 'strategy',
    placeholder:
      'State this quarter\'s and this year\'s revenue targets, split by product line if possible. Example: "Q2 target £350k — £220k from Social Go/Pro listings, £90k from Social Week MRR, £40k from Presentour."',
  },
  {
    key: 'client_objections',
    title: 'Client objections',
    category: 'sales',
    placeholder:
      'List the three to five objections you hear most often and the answer that actually closes the sale. Example: "We already have someone doing social — our agents are seeing X% more engagement than in-house output."',
  },
];

function CsvUploadPanel({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { ok: bool, msg: string }
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      setStatus({ ok: false, msg: 'Only .csv files are accepted.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ ok: false, msg: 'File is larger than the 5 MB limit.' });
      return;
    }
    setBusy(true); setStatus(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'data');
    try {
      const res = await axios.post(`${API}/api/derek/memory/csv`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus({ ok: true, msg: `Derek absorbed "${res.data.title}".` });
      if (onUploaded) onUploaded(res.data);
    } catch (e) {
      setStatus({
        ok: false,
        msg: e?.response?.data?.detail || 'Upload failed.',
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section
      className={`dkk-csv ${dragOver ? 'dkk-csv-drag' : ''}`}
      data-testid="dkk-csv"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
      }}
    >
      <div className="dkk-csv-icon">
        <FileSpreadsheet size={22} />
      </div>
      <div className="dkk-csv-copy">
        <h3>Upload a CSV report</h3>
        <p>
          Drop a revenue export, client list or pipeline CSV here.
          Derek reads the columns, totals, and a sample — and will cite it in future answers.
        </p>
      </div>
      <div className="dkk-csv-actions">
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="dkk-csv-input"
        />
        <button
          type="button"
          className="dkk-btn dkk-btn-primary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="dkk-csv-btn"
        >
          {busy ? <Loader2 size={16} className="dkk-spin" /> : <Upload size={16} />}
          {busy ? 'Uploading…' : 'Upload CSV'}
        </button>
        {status && (
          <div
            className={`dkk-csv-status ${status.ok ? 'dkk-csv-ok' : 'dkk-csv-err'}`}
            data-testid="dkk-csv-status"
          >
            {status.ok ? <CheckCircle2 size={14} /> : <X size={14} />}
            <span>{status.msg}</span>
          </div>
        )}
      </div>
    </section>
  );
}

function SuggestionsPanel({ onPick, existingCategories }) {
  return (
    <section className="dkk-suggestions" data-testid="dkk-suggestions">
      <header className="dkk-suggestions-head">
        <Lightbulb size={14} />
        <h3>Suggested Knowledge to Add</h3>
        <span className="dkk-suggestions-sub">
          Click any card to start teaching Derek
        </span>
      </header>
      <div className="dkk-suggestions-grid">
        {KNOWLEDGE_SUGGESTIONS.map((s) => {
          const alreadyCovered = existingCategories.has(s.category);
          return (
            <button
              key={s.key}
              type="button"
              className={`dkk-suggestion ${alreadyCovered ? 'dkk-suggestion-covered' : ''}`}
              onClick={() => onPick(s)}
              data-testid={`dkk-suggestion-${s.key}`}
              title={alreadyCovered ? 'Derek already has notes on this — add more?' : 'Click to add'}
            >
              <div className="dkk-suggestion-icon">
                <Sparkles size={14} />
              </div>
              <div className="dkk-suggestion-body">
                <span className="dkk-suggestion-title">{s.title}</span>
                {alreadyCovered && (
                  <span className="dkk-suggestion-tag">Expand further</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function EntryModal({ entry, prefill, onClose, onSaved }) {
  const [title, setTitle] = useState(entry?.title || prefill?.title || '');
  const [content, setContent] = useState(entry?.content || '');
  const [category, setCategory] = useState(entry?.category || prefill?.category || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!title.trim() || !content.trim()) {
      setError('Title and content are required.');
      return;
    }
    setSaving(true); setError('');
    const payload = {
      title: title.trim(),
      content: content.trim(),
      category: category.trim() || null,
    };
    try {
      const url = entry
        ? `${API}/api/derek/memory/${entry.id}`
        : `${API}/api/derek/memory`;
      const method = entry ? 'put' : 'post';
      const res = await axios[method](url, payload);
      onSaved(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const placeholder = prefill?.placeholder
    || 'Tell Derek what he needs to know. Keep it precise — he quotes back what you give him.';

  return (
    <div className="dkk-modal-backdrop" onClick={onClose}>
      <div
        className="dkk-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="dkk-modal"
      >
        <div className="dkk-modal-head">
          <div>
            <div className="dkk-modal-eyebrow">
              {entry ? 'Edit knowledge' : prefill ? 'Suggested knowledge' : 'New knowledge'}
            </div>
            <h2>{entry ? 'Refine what Derek knows' : prefill ? prefill.title : 'Teach Derek something new'}</h2>
          </div>
          <button
            className="dkk-icon-btn"
            onClick={onClose}
            data-testid="dkk-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="dkk-modal-body">
          <label className="dkk-field">
            <span>Title</span>
            <input
              className="dkk-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q2 Pricing Decision"
              data-testid="dkk-title-input"
              maxLength={240}
            />
          </label>
          <label className="dkk-field">
            <span>Category <em>· optional</em></span>
            <input
              className="dkk-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="pricing, strategy, product, team…"
              data-testid="dkk-category-input"
              maxLength={60}
            />
          </label>
          <label className="dkk-field">
            <span>Content</span>
            <textarea
              className="dkk-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={placeholder}
              rows={8}
              data-testid="dkk-content-input"
              maxLength={20000}
            />
          </label>
          {error && (
            <div className="dkk-error" data-testid="dkk-error">{error}</div>
          )}
        </div>
        <div className="dkk-modal-foot">
          <button
            className="dkk-btn dkk-btn-ghost"
            onClick={onClose}
            data-testid="dkk-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="dkk-btn dkk-btn-primary"
            onClick={submit}
            disabled={saving}
            data-testid="dkk-save-btn"
          >
            {saving ? <Loader2 size={16} className="dkk-spin" /> : <Save size={16} />}
            {entry ? 'Save changes' : 'Teach Derek'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DerekKnowledge({ onChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/derek/memory`);
      setEntries(res.data.entries || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const existingCategories = new Set(
    entries.map((e) => (e.category || '').toLowerCase()).filter(Boolean)
  );

  const onSaved = () => {
    setModalOpen(false);
    setEditing(null);
    setPrefill(null);
    load();
    if (onChange) onChange();
  };

  const remove = async (entry) => {
    if (!window.confirm(`Forget "${entry.title}"? Derek won't reference this again.`)) return;
    try {
      await axios.delete(`${API}/api/derek/memory/${entry.id}`);
      load();
      if (onChange) onChange();
    } catch { /* noop */ }
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setPrefill(null);
    setModalOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setPrefill(null);
    setModalOpen(true);
  };

  const openFromSuggestion = (suggestion) => {
    setEditing(null);
    setPrefill(suggestion);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setPrefill(null);
  };

  return (
    <section className="dkk-wrap" data-testid="dkk-wrap">
      <header className="dkk-header">
        <div>
          <div className="dkk-eyebrow">
            <BookOpen size={13} /> Training Room
          </div>
          <h2 className="dkk-h1">Derek Knowledge</h2>
          <p className="dkk-sub">
            Everything you teach Derek here is loaded into his brain on every reply.
          </p>
        </div>
        <button
          className="dkk-btn dkk-btn-primary dkk-btn-lg"
          onClick={openNew}
          data-testid="dkk-new-btn"
        >
          <Plus size={16} /> Add knowledge
        </button>
      </header>

      <SuggestionsPanel onPick={openFromSuggestion} existingCategories={existingCategories} />

      <CsvUploadPanel onUploaded={() => { load(); if (onChange) onChange(); }} />

      {loading ? (
        <div className="dkk-loading">
          <Loader2 className="dkk-spin" size={20} /> Loading Derek's memory…
        </div>
      ) : entries.length === 0 ? (
        <div className="dkk-empty" data-testid="dkk-empty">
          <BookOpen size={42} strokeWidth={1.25} />
          <h3>Derek has no stored knowledge yet</h3>
          <p>
            Start with one of the suggestions above, or write your own entry.
            Each one sharpens every reply.
          </p>
          <button className="dkk-btn dkk-btn-primary" onClick={openNew}>
            <Plus size={16} /> Add your first
          </button>
        </div>
      ) : (
        <div className="dkk-grid" data-testid="dkk-grid">
          {entries.map((entry) => (
            <article
              key={entry.id}
              className="dkk-card"
              data-testid={`dkk-card-${entry.id}`}
              onClick={() => openEdit(entry)}
            >
              <div className="dkk-card-glow" />
              <div className="dkk-card-head">
                {entry.category && (
                  <span className="dkk-chip">
                    <Folder size={11} /> {entry.category}
                  </span>
                )}
                <div className="dkk-card-actions">
                  <button
                    className="dkk-icon-btn"
                    title="Edit"
                    onClick={(e) => { e.stopPropagation(); openEdit(entry); }}
                    data-testid={`dkk-edit-${entry.id}`}
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    className="dkk-icon-btn"
                    title="Delete"
                    onClick={(e) => { e.stopPropagation(); remove(entry); }}
                    data-testid={`dkk-delete-${entry.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <h3 className="dkk-card-title">{entry.title}</h3>
              <p className="dkk-card-preview">{entry.content}</p>
              <div className="dkk-card-foot">
                <span>Updated {new Date(entry.updated_at).toLocaleDateString()}</span>
              </div>
            </article>
          ))}
        </div>
      )}

      {modalOpen && (
        <EntryModal
          entry={editing}
          prefill={prefill}
          onClose={closeModal}
          onSaved={onSaved}
        />
      )}
    </section>
  );
}
